import { useCallback } from "react";
import { ReportData } from "../../types/global.d";
import { getReportColor } from "../../utils/reportProps";
import { formatClusterCount } from "../map/mapClustering";

export const useMarkerIcon = () => {
  // Inject CSS for marker animations if not already injected
  const injectMarkerStyles = useCallback(() => {
    if (document.getElementById("marker-styles")) return;

    const style = document.createElement("style");
    style.id = "marker-styles";
    style.textContent = `
      @keyframes pulse-ring {
        0% {
          transform: translate(-50%, -50%) scale(1);
          opacity: 0.8;
        }
        100% {
          transform: translate(-50%, -50%) scale(1.8);
          opacity: 0;
        }
      }

      @keyframes pulse-glow {
        0%, 100% {
          box-shadow: 0 0 0 4px rgba(163, 191, 97, 0.3),
                      0 0 0 8px rgba(163, 191, 97, 0.2),
                      0 0 20px rgba(163, 191, 97, 0.4),
                      0 4px 12px rgba(0, 0, 0, 0.15);
        }
        50% {
          box-shadow: 0 0 0 6px rgba(163, 191, 97, 0.4),
                      0 0 0 12px rgba(163, 191, 97, 0.25),
                      0 0 30px rgba(163, 191, 97, 0.5),
                      0 4px 16px rgba(0, 0, 0, 0.2);
        }
      }

      .marker-pulse-ring {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(1);
        border-radius: 50%;
        border: 2px solid rgba(163, 191, 97, 0.7);
        animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        pointer-events: none;
      }

      .marker-selected {
        animation: pulse-glow 2s ease-in-out infinite;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
    `;
    document.head.appendChild(style);
  }, []);

  const createMarkerElement = useCallback(
    (report: ReportData, isSelected: boolean = false): HTMLElement => {
      injectMarkerStyles();

      const color = getReportColor(report);
      const baseSize = 20;
      const size = isSelected ? 32 : baseSize;
      const strokeWidth = isSelected ? 3 : 2;
      const strokeColor = isSelected ? "#FFFFFF" : "white";

      // Create container for marker and pulse ring
      const container = document.createElement("div");
      container.style.position = "relative";
      container.style.width = isSelected ? "48px" : "20px";
      container.style.height = isSelected ? "48px" : "20px";
      container.style.display = "flex";
      container.style.alignItems = "center";
      container.style.justifyContent = "center";
      container.style.cursor = "pointer";
      container.style.transition = "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)";

      // Create pulse ring for selected markers
      if (isSelected) {
        const pulseRing = document.createElement("div");
        pulseRing.className = "marker-pulse-ring";
        // Set size to match the marker size for proper scaling
        pulseRing.style.width = `${size}px`;
        pulseRing.style.height = `${size}px`;
        container.appendChild(pulseRing);
      }

      // Create the main marker element
      const markerElement = document.createElement("div");
      markerElement.style.width = `${size}px`;
      markerElement.style.height = `${size}px`;
      markerElement.style.borderRadius = "50%";
      markerElement.style.backgroundColor = color;
      markerElement.style.border = `${strokeWidth}px solid ${strokeColor}`;
      markerElement.style.position = "relative";
      markerElement.style.zIndex = "1";
      markerElement.style.transition = "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)";

      if (isSelected) {
        markerElement.className = "marker-selected";
        // Base glow effect - animation will enhance it
        markerElement.style.boxShadow = `
          0 0 0 4px rgba(163, 191, 97, 0.3),
          0 0 0 8px rgba(163, 191, 97, 0.2),
          0 0 20px rgba(163, 191, 97, 0.4),
          0 4px 12px rgba(0, 0, 0, 0.15)
        `;
        markerElement.style.transform = "scale(1)";
      } else {
        markerElement.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.2)";
      }

      container.appendChild(markerElement);
      return container;
    },
    [injectMarkerStyles]
  );

  // Build a cluster bubble: a white pill wrapped in a colored outline + count.
  // Size tiers mirror the mobile app (38 / 44 / 52 px at 10 / 100 thresholds).
  // The outline is drawn as a gradient "ring" (outer gradient layer + inner
  // white pill) so a mixed cluster shows ONE outline split half `color` /
  // half `secondaryColor`; a single-status cluster makes both halves `color`.
  const createClusterElement = useCallback(
    (count: number, color: string, secondaryColor?: string): HTMLElement => {
      const size = count >= 100 ? 52 : count >= 10 ? 44 : 38;
      const RING_WIDTH = 3;
      const isMixed = Boolean(secondaryColor);

      const ring = document.createElement("div");
      ring.style.display = "inline-flex";
      ring.style.alignItems = "stretch";
      ring.style.boxSizing = "border-box";
      ring.style.minWidth = `${size}px`;
      ring.style.height = `${size}px`;
      ring.style.padding = `${RING_WIDTH}px`;
      ring.style.borderRadius = "999px";
      ring.style.background = `linear-gradient(90deg, ${color} 0 50%, ${
        secondaryColor ?? color
      } 50% 100%)`;
      ring.style.cursor = "pointer";
      ring.style.boxShadow = "0 3px 8px rgba(0, 0, 0, 0.22)";

      const inner = document.createElement("div");
      inner.style.flex = "1";
      inner.style.display = "flex";
      inner.style.alignItems = "center";
      inner.style.justifyContent = "center";
      inner.style.boxSizing = "border-box";
      inner.style.padding = "0 6px";
      inner.style.borderRadius = "999px";
      inner.style.backgroundColor = "#FFFFFF";

      const label = document.createElement("span");
      label.textContent = formatClusterCount(count);
      // Neutral text for a mixed cluster so neither status color dominates.
      label.style.color = isMixed ? "#4A4D40" : color;
      label.style.fontWeight = "800";
      label.style.fontSize = "14px";
      label.style.lineHeight = "1";

      inner.appendChild(label);
      ring.appendChild(inner);
      return ring;
    },
    []
  );

  return { createMarkerElement, createClusterElement };
};
